import type { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model'
import type { EditorState } from '@tiptap/pm/state'

type DocumentRange = { from: number; to: number }

const resolvedPositionIsInCodeBlock = ($position: ResolvedPos): boolean => {
    for (let depth = $position.depth; depth > 0; depth -= 1) {
        const node = $position.node(depth)
        if (node.type.name === 'codeBlock' || node.type.spec.code === true) return true
    }

    return false
}

const backtickRunIsEscaped = (text: string, index: number): boolean => {
    let precedingBackslashes = 0
    for (let cursor = index - 1; cursor >= 0 && text[cursor] === '\\'; cursor -= 1) {
        precedingBackslashes += 1
    }
    return precedingBackslashes % 2 === 1
}

export const textHasOpenBacktickLiteral = (text: string): boolean => {
    let openDelimiterLength: number | null = null
    const backtickRuns = /`+/g
    let match: RegExpExecArray | null

    while ((match = backtickRuns.exec(text)) !== null) {
        if (backtickRunIsEscaped(text, match.index)) continue

        const delimiterLength = match[0].length
        if (openDelimiterLength === null) {
            openDelimiterLength = delimiterLength
        } else if (openDelimiterLength === delimiterLength) {
            openDelimiterLength = null
        }
    }

    return openDelimiterLength !== null
}

const positionFollowsOpenBackticks = (doc: ProseMirrorNode, position: number): boolean => {
    const boundedPosition = Math.max(0, Math.min(position, doc.content.size))
    const $position = doc.resolve(boundedPosition)
    if (!$position.parent.isTextblock) return false

    const textBefore = $position.parent.textBetween(0, $position.parentOffset, '', '\ufffc')
    return textHasOpenBacktickLiteral(textBefore)
}

const rangeContainsCodeMark = (doc: ProseMirrorNode, range: DocumentRange): boolean => {
    if (range.to <= range.from) return false

    let containsCodeMark = false
    doc.nodesBetween(range.from, range.to, node => {
        if (node.isText && node.marks.some(mark => mark.type.name === 'code')) {
            containsCodeMark = true
            return false
        }
        return !containsCodeMark
    })
    return containsCodeMark
}

export const rangeIsInsideCodeLiteral = (state: EditorState, range: DocumentRange): boolean => {
    const docSize = state.doc.content.size
    const from = Math.max(0, Math.min(range.from, docSize))
    const to = Math.max(from, Math.min(range.to, docSize))
    const lastContentPosition = Math.max(from, to - 1)
    const $from = state.doc.resolve(from)
    const $to = state.doc.resolve(lastContentPosition)

    if (resolvedPositionIsInCodeBlock($from) || resolvedPositionIsInCodeBlock($to)) return true
    if (rangeContainsCodeMark(state.doc, { from, to })) return true

    if (to === from) {
        const activeMarks = [...$from.marks(), ...(state.storedMarks || [])]
        if (activeMarks.some(mark => mark.type.name === 'code')) return true
    }

    return positionFollowsOpenBackticks(state.doc, from)
}
